import {
  GeneralRequirementType,
  PayeeTaxDocumentSharePermission,
  PayerPayeeStatus,
  RequirementStatus,
  VerificationStatus
} from "@wingspanhq/payments/dist/interfaces";
import {
  CustomGeneralRequirementItem,
  CustomRequirementType,
  ExtendedRequirementItem
} from "../modules/GeneralRequirements/types";
import { usePayerQuery } from "../../../query/payers/queries/usePayerQuery";
import { useQueryPayerRows } from "../../../query/search/payer/queries/useQueryPayerRows";
import { useQueryPayerRowsSummary } from "../../../query/search/payer/queries/useQueryPayerRowsSummary";
import { useUserId } from "../../../query/hooks/helpers";
import { usePaymentsStatusQuery } from "../../../query/payments/queries";
import { useQueryVerifications } from "../../../query/onboarding/queries/useQueryVerifications";

export const usePayersExtendedGeneralRequirements = (
  firstPayerId?: string
): {
  requirements: ExtendedRequirementItem[];
  isLoading: boolean;
} => {
  const queryPayer = usePayerQuery(firstPayerId || "", {
    enabled: !!firstPayerId
  });

  const payer = queryPayer.data;

  const queryAllPayersSummary = useQueryPayerRowsSummary({
    filter: {
      "payeeOwnedData.status": PayerPayeeStatus.Active
    }
  });

  const queryPayersWithSharedInfoSummary = useQueryPayerRowsSummary({
    filter: {
      "payeeOwnedData.status": PayerPayeeStatus.Active,
      "payeeOwnedData.shareTaxDocument": PayeeTaxDocumentSharePermission.Allow
    }
  });

  const queryPaymentsSettings = usePaymentsStatusQuery();
  const oldTaxInfoStatus =
    queryPaymentsSettings.data?.verifications.taxDocumentation;

  const queryVerifications = useQueryVerifications();
  const taxInfoStatus = queryVerifications.data?.tax;

  return {
    requirements: queryPayer.data
      ? [
          {
            requirementType: GeneralRequirementType.Registration,
            status:
              payer?.requirements?.find(
                r => r.requirementType === GeneralRequirementType.Registration
              )?.status || RequirementStatus.Incomplete
          },
          {
            requirementType: GeneralRequirementType.PayoutMethod,
            status:
              payer?.requirements?.find(
                r => r.requirementType === GeneralRequirementType.PayoutMethod
              )?.status || RequirementStatus.Incomplete
          },
          {
            requirementType: CustomRequirementType.TaxForm,
            status: [oldTaxInfoStatus, taxInfoStatus].includes(
              VerificationStatus.Verified
            )
              ? RequirementStatus.Complete
              : RequirementStatus.Incomplete
          },
          {
            requirementType: CustomRequirementType.InfoShare,
            status:
              queryPayersWithSharedInfoSummary.data?.listSize ===
              queryAllPayersSummary.data?.listSize
                ? RequirementStatus.Complete
                : RequirementStatus.Incomplete,
            // need for status secondary text
            completedPayersLength:
              queryPayersWithSharedInfoSummary.data?.listSize,
            allPayersLength: queryAllPayersSummary.data?.listSize
          }
        ]
      : [],
    isLoading:
      queryPayer.isLoading ||
      queryAllPayersSummary.isLoading ||
      queryPayersWithSharedInfoSummary.isLoading ||
      queryPaymentsSettings.isLoading ||
      queryVerifications.isLoading
  };
};
